import { financialManagementAccountList } from 'apis/fms';
import { useEffect, useMemo, useState } from 'react';
import { FmsAccountManagement } from 'types/fms';
import {
  formatAmount,
  InvoiceItemsTable,
  InvoiceLayout,
  OrderDetailBatchReturnType,
  formatInvoiceDate,
  useInvoiceRecipientAddress,
} from './common';

const normalizeKey = (value?: string | number | null) => {
  if (value === undefined || value === null) return '';
  return String(value).replace(/\s+/g, '').toUpperCase();
};

const SGGXInvoice = ({ order }: { order: OrderDetailBatchReturnType }) => {
  const [paymentAccountList, setPaymentAccountList] = useState<
    FmsAccountManagement[]
  >([]);
  const subTotal = order.payAmountActualCurrency ?? 0;
  const { cityLabel, countryLabel } = useInvoiceRecipientAddress(order);
  const actualCurrencyRate = order.orderItems[0]?.actualCurrencyRate ?? 1;
  const integrationDeduction = order.useIntegration
    ? Math.round(order.useIntegration * actualCurrencyRate)
    : 0;
  const summaryRows = [
    order.freightAmountActualCurrency
      ? {
          label: 'Freight :',
          value: formatAmount(order.freightAmountActualCurrency),
        }
      : null,
    order.promotionAmountActualCurrency
      ? {
          label: 'Discount :',
          value: `-${formatAmount(order.promotionAmountActualCurrency)}`,
        }
      : null,
    integrationDeduction
      ? {
          label: 'Points Deduction :',
          value: `-${formatAmount(integrationDeduction)}`,
        }
      : null,
    {
      label: 'Sub Total Amount :',
      value: formatAmount(subTotal),
    },
  ].filter((row): row is { label: string; value: string } => Boolean(row));

  const selectedPaymentCodes = useMemo(() => {
    const set = new Set<string>();

    order.orderPayList?.forEach((payment) => {
      const typeKey = normalizeKey(payment.payType);
      if (typeKey) set.add(typeKey);
    });

    return set;
  }, [order.orderPayList]);

  useEffect(() => {
    if (!order.createdFrom) return;

    financialManagementAccountList(order.createdFrom).then((res) => {
      const list = [...res].sort((a, b) => a.sort - b.sort);
      setPaymentAccountList(list);
    });
  }, [order.createdFrom]);

  const displayPaymentOptions = useMemo(() => {
    return paymentAccountList
      .map((account, index) => ({
        key: account.accountCode,
        label: account.accountName,
        index,
      }))
      .sort((a, b) => {
        const aSelected =
          selectedPaymentCodes.has(normalizeKey(a.key)) ||
          selectedPaymentCodes.has(normalizeKey(a.label));
        const bSelected =
          selectedPaymentCodes.has(normalizeKey(b.key)) ||
          selectedPaymentCodes.has(normalizeKey(b.label));

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return a.index - b.index;
      });
  }, [paymentAccountList, selectedPaymentCodes]);

  return (
    <InvoiceLayout>
      <header className="mb-[30px] text-center">
        <p className="text-lg font-bold mb-1.5">GINZAXIAOMA SG PTE. LTD.</p>
        <p className="text-xs mb-1.5">Co Reg. No. 202214052E</p>
        <p className="text-xs mb-1.5">
          391B Orchard Road #23-05 Ngee Ann City Singapore 238874
        </p>
        <p className="text-xs mb-4">Tel : +65 6530 3529 Fax : +65 65303528</p>
        <p className="text-[28px] font-bold">SALES INVOICE</p>
      </header>

      <section className="mb-[30px] flex justify-between">
        <div>
          <div className="font-bold mb-2 text-xs">To :</div>
          <div className="text-xs leading-[1.6]">
            {order.receiverName}
            {order.receiverDetailAddress ? (
              <>
                <br />
                {order.receiverDetailAddress}
              </>
            ) : null}
            {cityLabel || countryLabel ? (
              <>
                <br />
                {cityLabel} {countryLabel}
              </>
            ) : null}
            {order.receiverPhone ? (
              <>
                <br />
                {order.receiverPhone}
              </>
            ) : null}
          </div>
        </div>
        <div className="w-[220px] shrink-0 text-xs text-right space-y-2">
          <div className="whitespace-nowrap">
            <span className="font-bold">Invoice no :</span>
            <span className="ml-2">{order.invoiceNo}</span>
          </div>
          <div>
            <span className="font-bold">Date :</span>
            <span className="ml-2">
              {formatInvoiceDate(order, 'YYYY/M/D')}
            </span>
          </div>
        </div>
      </section>

      <InvoiceItemsTable
        order={order}
        firstColumnLabel="Quantity"
        renderFirstCell={(item) => item.productQuantity}
      />

      <section className="ml-auto mr-[6px] w-[330px] text-xs mb-3">
        {summaryRows.map((row) => (
          <div
            className="grid grid-cols-[1fr_112px] gap-x-8 mb-2 last:mb-0"
            key={row.label}
          >
            <span className="text-right whitespace-nowrap font-bold">
              {row.label}
            </span>
            <span className="text-right tabular-nums">{row.value}</span>
          </div>
        ))}
      </section>

      <section className="text-xs">
        <div className="flex justify-between gap-x-8 border-t border-black pt-2">
          <div className="flex-1">
            <div className="font-bold mb-1">Payment mode</div>
            <div className="space-y-1">
              {displayPaymentOptions.map((option) => {
                const checked =
                  selectedPaymentCodes.has(normalizeKey(option.key)) ||
                  selectedPaymentCodes.has(normalizeKey(option.label));

                return (
                  <div className="flex items-center gap-1" key={option.key}>
                    <span className="inline-flex h-3 w-3 items-center justify-center border border-black text-xs leading-none">
                      {checked ? '✓' : ''}
                    </span>
                    <span>{option.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-start gap-1">
            <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center border border-black text-xs leading-none">
              ✓
            </span>
            <span>
              Goods sold are not eligible for <br />
              refund, return, or exchange.
            </span>
          </div>
        </div>

        <div className="my-4 grid grid-cols-[80px_1fr] gap-x-8">
          <span className="font-bold">Paynow</span>
          <span>202214052EOCB</span>
        </div>

        <div className="mt-4 gap-x-8">
          <div className="font-bold mb-1.5">Bank Transfer</div>
          <div className="grid grid-cols-[80px_1fr] gap-y-1 gap-x-8">
            <span className="font-bold">Beneficiary</span>
            <span>GINZAXIAOMA SG PTE. LTD.</span>
            <span className="font-bold">A/C NO</span>
            <span>595048950001</span>
            <span className="font-bold">Bank</span>
            <span>OCBC</span>
          </div>
        </div>
      </section>
    </InvoiceLayout>
  );
};

export default SGGXInvoice;
